/**
 * Settings that live on an integration's own document rather than on team
 * settings, catalogued against the vendor group they are configured under.
 * groups.ts mirrors the connection cards; this describes what a merchant can
 * change once a vendor is connected.
 */
import { createSettingMetadataFromGroupDefaults } from "../settings-catalog/metadata";
import type {
  ExternalSettingValueSource,
  SettingAccess,
  SettingAgentEditability,
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
} from "../settings-catalog/types";
import {
  IntegrationsHubSettingGroups,
  type IntegrationsHubSettingGroupId,
} from "./groups";

/** The vendor groups themselves are read-only cards; these are editable. */
const merchantEditableOnVendorPage: SettingAccess = {
  read: { minVisibility: "merchant" },
  write: {
    mode: "grants",
    grants: [{ minVisibility: "merchant", method: "direct" }],
  },
};

const requiresApproval: SettingAgentEditability = {
  mode: "requiresApproval",
  reason:
    "Changes where Redo posts lines in the merchant's ERP, which their finance team reconciles against.",
};

const linnworksReturnsRequiresApproval: SettingAgentEditability = {
  mode: "requiresApproval",
  reason:
    "Changes whether and where the merchant's warehouse receives returned stock in Linnworks.",
};

interface IntegrationsHubSettingInput extends SettingRegistryMetadataInput {
  group: IntegrationsHubSettingGroupId;
  /** Path on the integration document, for the integration value adapter. */
  ref: string;
}

const teamworkReturnSettingDefaults = {
  group: "integrations.teamwork-commerce",
  access: merchantEditableOnVendorPage,
  agentEditability: {
    mode: "requiresApproval",
    reason:
      "Changes Teamwork return documents and who issues customer refunds or store credit.",
  },
  risk: {
    level: "high",
    reason:
      "Incorrect mappings can affect refund payouts, gift cards, and inventory reconciliation.",
  },
} as const;

const IntegrationsHubRegistry = {
  "netsuite/exchangeOrder/discountItemId": {
    group: "integrations.netsuite",
    ref: "netsuite.exchangeOrderSettings.discountItemId",
    label: "Exchange order header discount item ID",
    description:
      "The NetSuite internal ID of the Discount item Redo sets as the header (order-level) discount on temporary and final exchange Sales Orders to apply the pre-tax returned value. Product discounts on individual exchange items use it as discount lines. It is required only when an exchange needs a discount.",
    synonyms: [
      "exchange order header discount item id",
      "exchange header discount item",
      "exchange discount item",
      "netsuite exchange discount",
      "exchange order discount item",
      "discount item id",
    ],
    access: merchantEditableOnVendorPage,
    agentEditability: requiresApproval,
  },
  "netsuite/exchangeOrder/returnValueApplication": {
    group: "integrations.netsuite",
    ref: "netsuite.exchangeOrderSettings.returnValueApplication",
    label: "Apply returned value to exchange orders",
    description:
      "How Redo gives an exchange Sales Order the value of the returned items. A header discount lowers the order before NetSuite taxes it. A credit memo keeps the order at full price, credits the returned value with its original tax from the return authorization, and applies it to the order's invoice once the order is fulfilled, so the order's tax matches the shipping address.",
    synonyms: [
      "exchange credit memo",
      "returned value application",
      "apply returned value after tax",
      "exchange invoice credit",
    ],
    access: merchantEditableOnVendorPage,
    agentEditability: requiresApproval,
  },
  "netsuite/exchangeOrder/customerDepositAccountId": {
    group: "integrations.netsuite",
    ref: "netsuite.exchangeOrderSettings.customerDepositAccountId",
    label: "Exchange payment deposit account ID",
    description:
      "The NetSuite internal ID of the account Redo posts the Customer Deposit to when a shopper pays an exchange balance. Leave it empty to use Undeposited Funds. Used only when the returned value is applied with a credit memo.",
    synonyms: [
      "exchange customer deposit account",
      "exchange deposit account",
      "exchange payment account",
    ],
    access: merchantEditableOnVendorPage,
    agentEditability: requiresApproval,
  },
  "netsuite/exchangeOrder/customerDepositPaymentMethodId": {
    group: "integrations.netsuite",
    ref: "netsuite.exchangeOrderSettings.customerDepositPaymentMethodId",
    label: "Exchange payment deposit payment method ID",
    description:
      "The NetSuite internal ID of the payment method Redo sets on the Customer Deposit for a shopper's exchange balance payment. Optional. Used only when the returned value is applied with a credit memo.",
    synonyms: [
      "exchange deposit payment method",
      "exchange customer deposit payment method",
    ],
    access: merchantEditableOnVendorPage,
    agentEditability: requiresApproval,
  },
  "netsuite/useDiscountLocationId": {
    group: "integrations.netsuite",
    ref: "netsuite.useDiscountLocationId",
    label: "Match the discount line's location to the returned items",
    description:
      "Where Redo posts the discount line on a NetSuite return authorization. On, it goes to the location of the items being returned; off, it goes to the account's first location. Turn it on if NetSuite rejects a return authorization because its discount line names an inactive location or one on another subsidiary.",
    synonyms: [
      "discount location",
      "discount line location",
      "netsuite discount location",
      "match discount location",
    ],
    access: merchantEditableOnVendorPage,
    agentEditability: requiresApproval,
    valueSemantics: {
      defaultValue: false,
      falseLabel: "The account's first location",
      trueLabel: "The location of the returned items",
    },
  },
  "netsuite/customerRefund/customFields": {
    group: "integrations.netsuite",
    ref: "netsuite.customerRefund.customFieldsOnCustomerRefund",
    label: "Custom fields on customer refund",
    description:
      "Adds merchant-defined body-level fields to NetSuite customer refunds. Values can use dynamic variables, nested objects, output types, and optional value mappings such as translating a restock decision to a custom-list option ID.",
    access: merchantEditableOnVendorPage,
    agentEditability: requiresApproval,
  },
  "netsuite/returnAuthorization/sourcePriority": {
    group: "integrations.netsuite",
    ref: "netsuite.returnAuthorization.sourcePriority",
    label: "Create return authorizations from",
    description:
      "The NetSuite records a return authorization may be built from, in order of preference: invoice, sales order, or cash sale. The first listed record that exists for the order is used; a record that is not listed is never used, so the return waits for a listed one instead.",
    synonyms: [
      "create return authorizations from",
      "return authorization source",
      "rma from invoice",
      "rma from sales order",
    ],
    access: merchantEditableOnVendorPage,
    agentEditability: requiresApproval,
  },
  "linnworks/returns/enabled": {
    group: "integrations.linnworks",
    ref: "linnworks.returns.enabled",
    label: "Create RMAs in Linnworks",
    description:
      "Creates an RMA against the original order in Linnworks for each Redo return, so the warehouse can receive the items and the order shows as returned.",
    synonyms: [
      "create rmas in linnworks",
      "linnworks rma",
      "linnworks returns sync",
      "sync returns to linnworks",
    ],
    access: merchantEditableOnVendorPage,
    agentEditability: linnworksReturnsRequiresApproval,
    valueSemantics: {
      defaultValue: false,
      falseLabel: "Returns don't create Linnworks RMAs",
      trueLabel: "Returns create Linnworks RMAs",
    },
  },
  "linnworks/returns/processReturnsEnabled": {
    group: "integrations.linnworks",
    ref: "linnworks.returns.processReturnsEnabled",
    label: "Process returns when actioned in Linnworks",
    description:
      "Refunds or issues credit for a return once the warehouse actions its RMA in Linnworks. Linnworks restocks the items, so Redo doesn't restock them again.",
    synonyms: [
      "process returns when actioned in linnworks",
      "linnworks auto process returns",
      "refund when linnworks rma is actioned",
    ],
    access: merchantEditableOnVendorPage,
    agentEditability: linnworksReturnsRequiresApproval,
    valueSemantics: {
      defaultValue: true,
      falseLabel: "Staff process returns in Redo",
      trueLabel: "Actioned Linnworks RMAs process the return",
    },
  },
  "linnworks/returns/defaultLocation": {
    group: "integrations.linnworks",
    ref: "linnworks.returns.locations.defaultLocation",
    label: "Default Linnworks returns location",
    description:
      "The Linnworks location (and returns bin, for warehouse-managed locations) that receives a return unless the Redo location it comes back to has its own mapping. Required to turn on Linnworks RMAs.",
    synonyms: [
      "default linnworks returns location",
      "linnworks returns bin",
      "linnworks binrack",
    ],
    access: merchantEditableOnVendorPage,
    agentEditability: linnworksReturnsRequiresApproval,
  },
  "linnworks/returns/locationMappings": {
    group: "integrations.linnworks",
    ref: "linnworks.returns.locations.mappings",
    label: "Linnworks returns location by Redo location",
    description:
      "Sends returns from a specific Redo location, such as a shop, to its own Linnworks location (and returns bin, for warehouse-managed locations) instead of the default.",
    synonyms: [
      "linnworks returns location by redo location",
      "linnworks location mapping",
      "linnworks shop returns bin",
    ],
    access: merchantEditableOnVendorPage,
    agentEditability: linnworksReturnsRequiresApproval,
  },
  "teamworkCommerce/processing/enabled": {
    ...teamworkReturnSettingDefaults,
    ref: "teamworkCommerce.processingSettings.enabled",
    label: "Use Teamwork return processing",
    description:
      "Creates Teamwork return receipts and RMA worksheets from the settings below. When off, returns create the same return receipts as before.",
    synonyms: ["Teamwork return processing"],
  },
  "teamworkCommerce/processing/customOrders/paymentGateways": {
    ...teamworkReturnSettingDefaults,
    ref: "teamworkCommerce.processingSettings.customOrders.paymentGateways",
    label: "Payment methods handled by Teamwork",
    description:
      "Teamwork processes a custom order only when every payment on it is listed. Shopify orders do not use this filter, and manual review rules take priority.",
    synonyms: [
      "Teamwork payment gateways",
      "Teamwork custom order payment methods",
    ],
  },
  "teamworkCommerce/processing/customOrders/manualReviewPaymentGateways": {
    ...teamworkReturnSettingDefaults,
    ref: "teamworkCommerce.processingSettings.customOrders.manualReviewPaymentGateways",
    label: "Payment methods requiring review",
    description:
      "Custom-order returns are held for merchant approval when any payment matches, such as CASH.",
    synonyms: [
      "Teamwork manual review payment gateways",
      "Teamwork payment methods requiring review",
    ],
  },
  "teamworkCommerce/processing/customOrders/refundDocument": {
    ...teamworkReturnSettingDefaults,
    ref: "teamworkCommerce.processingSettings.customOrders.refundDocument",
    label: "Refund custom orders with",
    description:
      "An RMA worksheet has Teamwork refund the original payment. A return receipt records a refund issued outside Teamwork. Shopify orders are always refunded by Shopify.",
    synonyms: ["Teamwork custom order refunds", "Teamwork worksheets"],
  },
  "teamworkCommerce/processing/storeCredit/issuer": {
    ...teamworkReturnSettingDefaults,
    ref: "teamworkCommerce.processingSettings.storeCredit.issuer",
    label: "Issue store credit as",
    description:
      "Issue store credit as Teamwork gift cards created by the return receipt, or through your return settings' store credit.",
    synonyms: [
      "Teamwork store credit issuer",
      "Teamwork store credit provider",
    ],
  },
  "teamworkCommerce/processing/storeCredit/giftCards": {
    ...teamworkReturnSettingDefaults,
    ref: "teamworkCommerce.processingSettings.storeCredit.giftCards",
    label: "Gift card number format",
    description:
      "The number prefix and length for Teamwork gift cards issued by Redo. Cards use the return currency and split automatically at 2,000 per card.",
    synonyms: ["Teamwork gift cards", "Teamwork gift card settings"],
  },
  "teamworkCommerce/processing/receipt": {
    ...teamworkReturnSettingDefaults,
    ref: "teamworkCommerce.processingSettings.receipt",
    label: "Return receipts",
    description:
      "Return receipt settings: item identifiers, return and fill-from locations, original order lookup, refund and store credit payment codes, and adjustment charges.",
    synonyms: ["Teamwork return receipts"],
  },
  "teamworkCommerce/processing/worksheet": {
    ...teamworkReturnSettingDefaults,
    ref: "teamworkCommerce.processingSettings.worksheet",
    label: "RMA worksheets",
    description:
      "RMA worksheet settings for custom-order refunds and exchanges: item identifiers, return location, return condition, deduction charges, and exchange shipping method. Reason codes come from the return flow.",
    synonyms: ["Teamwork refund worksheets", "Teamwork RMA worksheet settings"],
  },
} as const satisfies Record<string, IntegrationsHubSettingInput>;

export type IntegrationsHubSettingKey = keyof typeof IntegrationsHubRegistry;

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const IntegrationsHubSettingGroupById = Object.fromEntries(
  IntegrationsHubSettingGroups.map((group) => [group.id, group]),
) as Record<
  IntegrationsHubSettingGroupId,
  (typeof IntegrationsHubSettingGroups)[number]
>;

export const IntegrationsHubSettingDefinitions =
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  (Object.keys(IntegrationsHubRegistry) as IntegrationsHubSettingKey[]).map(
    (key) => {
      const definition = IntegrationsHubRegistry[key];
      return {
        id: key,
        groupId: definition.group,
        metadata: createSettingMetadataFromGroupDefaults({
          setting: definition,
          group: IntegrationsHubSettingGroupById[definition.group],
        }),
        valueSource: {
          adapter: "integration",
          ref: definition.ref,
        } satisfies ExternalSettingValueSource,
      };
    },
  ) satisfies readonly SettingDefinition<
    IntegrationsHubSettingKey,
    IntegrationsHubSettingGroupId
  >[];

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const IntegrationsHubSettingMetadataByKey = Object.fromEntries(
  IntegrationsHubSettingDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<IntegrationsHubSettingKey, SettingMetadata>;
